import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Check, Copy, Download, Info, Instagram, Lock, Maximize, Heart, Share2, X, ZoomIn, ZoomOut, Sparkles, Pencil, Plus, Video, FileText, Loader2 } from 'lucide-react';
import { GENERATE_AUTH_TOKEN } from '../config';
import { supabase } from '../lib/supabase';
import SubscriptionLimitModal from './SubscriptionLimitModal';
import LoadingAnimation from './LoadingAnimation';
import { trackInteraction } from '../utils/trackInteraction';

// AI Information content component for the tooltip (copied from full modal)
const AIInfoContent = ({ isFashion = true }: { isFashion?: boolean }) => (
  <div className="flex flex-col space-y-3 sm:space-y-4">
    <div className="text-xs sm:text-sm">
      <p className="text-xs sm:text-sm">
        La inteligencia artificial es la tecnología de más rápida evolución en el mundo, por lo que mejora día a día.
        Sin embargo, como cualquier tecnología nueva, a veces no es perfecta. Queremos contarte de forma transparente sobre los detalles que estamos perfeccionando:
      </p>
      <ul className="list-disc pl-4 sm:pl-5 mt-1 sm:mt-2 space-y-0.5 sm:space-y-1">
        <li className="text-xs sm:text-sm">Rostros: Ocasionalmente, pueden aparecer pequeñas inconsistencias en los rasgos faciales.</li>
        <li className="text-xs sm:text-sm">Texto: El texto de tamaño reducido puede generarse de forma ilegible.</li>
        {isFashion ? (
          <>
            <li className="text-xs sm:text-sm">Fidelidad de la prenda: Pueden existir ligeras diferencias entre la prenda original y su representación en la imagen.</li>
            <li className="text-xs sm:text-sm">Restricciones de contenido: Para prevenir el uso inadecuado de la plataforma, hemos limitado la generación de imágenes con prendas de poca cobertura (como lencería o ropa interior).</li>
          </>
        ) : (
          <>
            <li className="text-xs sm:text-sm">Fidelidad del producto: Pueden existir ligeras diferencias entre el producto original y su representación en la imagen.</li>
            <li className="text-xs sm:text-sm">Restricciones de contenido: Para prevenir el uso inadecuado de la plataforma, hemos limitado la generación de imágenes con ciertos tipos de productos y configuraciones, como armas, objetos para adultos, y otros contenidos sensibles.</li>
          </>
        )}
      </ul>
      <p className="mt-1 sm:mt-2 text-xs sm:text-sm">
        Nuestro equipo, junto con expertos de toda la industria, trabaja sin descanso para que la IA sea cada vez más precisa y potente.
        Gracias por ser parte de este viaje y por confiar en nosotros.
      </p>
    </div>
    <div className="flex justify-center mt-3 sm:mt-4">
      <img 
        src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//Firma.png" 
        alt="Firma del equipo" 
        className="h-12 sm:h-16 object-contain" 
      />
    </div>
  </div>
);

interface GeneratedImage {
  id: string;
  fileName: string;
  signedUrl: string;
  favorito?: boolean;
}

interface GeneratedImagesInlineProps {
  imageIds: number[];
  companyId: string;
  onClose?: () => void;
  onReturnToConfig?: () => void;
  onGenerateMore?: () => Promise<void> | void;
  onReady?: () => void;
  preloadedImages?: GeneratedImage[] | null;
}

const GeneratedImagesInline: React.FC<GeneratedImagesInlineProps> = ({
  imageIds,
  companyId,
  onClose,
  onReturnToConfig,
  onGenerateMore,
  onReady,
  preloadedImages,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if we're on the dedicated page for full-width layout
  const isStandalonePage = location.pathname === '/redes-sociales/imagenes-generadas';

  useEffect(() => {
    try {
      console.log('[GeneratedImagesInline] mounted', {
        pathname: window.location.pathname,
        search: window.location.search,
      });
    } catch {}
    return () => {
      try { console.log('[GeneratedImagesInline] unmounted'); } catch {}
    };
  }, []);

  // Initialize states based on whether we have preloaded images
  const hasPreloadedOnMount = !!(preloadedImages && preloadedImages.length > 0);
  
  // State variables (kept in parity with full modal)
  const [loading, setLoading] = useState<boolean>(!hasPreloadedOnMount);
  const [images, setImages] = useState<GeneratedImage[]>(hasPreloadedOnMount ? preloadedImages : []);
  const [selectedImageIds, setSelectedImageIds] = useState<string[]>(hasPreloadedOnMount && preloadedImages.length > 0 ? [preloadedImages[0].id] : []);
  const [fullScreenImage, setFullScreenImage] = useState<GeneratedImage | null>(null);
  const [currentViewImage, setCurrentViewImage] = useState<GeneratedImage | null>(hasPreloadedOnMount && preloadedImages.length > 0 ? preloadedImages[0] : null);
  const [zoomLevel, setZoomLevel] = useState<number>(100);
  const [error, setError] = useState<string | null>(null);
  const [showInstagramPreview, setShowInstagramPreview] = useState<boolean>(false);
  const [instagramUsername, setInstagramUsername] = useState('instagram_username');
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [isPostingToInstagram, setIsPostingToInstagram] = useState(false);
  const [instagramPostSuccess, setInstagramPostSuccess] = useState(false);
  const [postDescription, setPostDescription] = useState('Moda fashion . veni a visitarnos');
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(postDescription);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const [isInstagramConnected, setIsInstagramConnected] = useState<boolean>(false);
  const [showConnectPrompt, setShowConnectPrompt] = useState<boolean>(false);
  const [isGeneratingMore, setIsGeneratingMore] = useState<boolean>(false);
  const [isGeneratingNewAngles, setIsGeneratingNewAngles] = useState<boolean>(false);
  const [newAnglesProgressComplete, setNewAnglesProgressComplete] = useState<boolean>(false);
  const [isFavoriting, setIsFavoriting] = useState<boolean>(false);
  const [showFavoriteMessage, setShowFavoriteMessage] = useState<boolean>(false);
  const [showMoreOptions, setShowMoreOptions] = useState<boolean>(false);
  const [showDescriptionPanel, setShowDescriptionPanel] = useState<boolean>(false);
  const [generatedDescription, setGeneratedDescription] = useState<string>('');
  const [copiedDescription, setCopiedDescription] = useState<boolean>(false);
  const [descriptionProgressActive, setDescriptionProgressActive] = useState<boolean>(false);
  const [showSubscriptionLimitModal, setShowSubscriptionLimitModal] = useState<boolean>(false);
  const [subscriptionLimitType, setSubscriptionLimitType] = useState<'free-trial-ended' | 'plan-limit-reached'>('free-trial-ended');
  const [showEditPlanRestriction, setShowEditPlanRestriction] = useState<boolean>(false);
  const [isFashionRubro, setIsFashionRubro] = useState<boolean>(true);

  // Animation states - always start at initial, trigger animation after mount if preloaded
  const [animationPhase, setAnimationPhase] = useState<'initial' | 'phase1' | 'phase2' | 'complete'>('initial');
  const [mainImageLoaded, setMainImageLoaded] = useState<boolean>(hasPreloadedOnMount);
  const [loadedThumbnails, setLoadedThumbnails] = useState<Set<number>>(hasPreloadedOnMount ? new Set(preloadedImages.map(img => img.id)) : new Set());
  const allThumbnailsLoaded = images.length > 0 && loadedThumbnails.size >= images.length;

  // Signal parent when animation has started (images are becoming visible)
  const hasSignaledReadyRef = useRef<boolean>(false);
  
  // Track if we've triggered the initial preload animation (to avoid re-triggering on "generate more")
  const hasTriggeredPreloadAnimationRef = useRef<boolean>(false);
  
  // Trigger animation after mount if preloaded (only once)
  useEffect(() => {
    if (hasPreloadedOnMount && animationPhase === 'initial' && !hasTriggeredPreloadAnimationRef.current) {
      hasTriggeredPreloadAnimationRef.current = true;
      // Small delay to ensure the initial state is rendered first, then animate
      const timer = setTimeout(() => {
        setAnimationPhase('phase1');
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [hasPreloadedOnMount, animationPhase]);
  
  useEffect(() => {
    // Signal ready when animation starts
    if (animationPhase === 'phase1' && !hasSignaledReadyRef.current) {
      hasSignaledReadyRef.current = true;
      onReady?.();
    }
  }, [animationPhase, onReady]);

  // Handle phase transitions with separate effect
  useEffect(() => {
    if (animationPhase === 'phase1') {
      const phase2Timer = setTimeout(() => {
        setAnimationPhase('phase2');
      }, 800);
      return () => clearTimeout(phase2Timer);
    }
    if (animationPhase === 'phase2') {
      const completeTimer = setTimeout(() => {
        setAnimationPhase('complete');
      }, 300);
      return () => clearTimeout(completeTimer);
    }
  }, [animationPhase]);

  // Ref for the zoomed image container
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [imagePosition, setImagePosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // Track the imageIds we've already loaded to avoid refetching
  const loadedImageIdsRef = useRef<string>(hasPreloadedOnMount ? imageIds.join(',') : '');

  // Combined effect: Reset state, load images (preloaded or fetch), and trigger animation
  useEffect(() => {
    if (imageIds.length > 0 && companyId) {
      const currentImageIdsKey = imageIds.join(',');
      const alreadyLoaded = loadedImageIdsRef.current === currentImageIdsKey;
      
      // Skip if we already have these images loaded (from preload or previous fetch)
      if (alreadyLoaded) {
        fetchCompanyLogo();
        checkInstagramConnection();
        return;
      }
      
      // Check if we have preloaded images that cover ALL requested imageIds
      const preloadedIdsSet = new Set(preloadedImages?.map(img => img.id) || []);
      const allIdsPreloaded = preloadedImages && preloadedImages.length > 0 && 
        imageIds.every(id => preloadedIdsSet.has(id));
      
      if (allIdsPreloaded) {
        // All images are preloaded - use them directly in the correct order
        const orderedImages = imageIds
          .map(id => preloadedImages!.find(img => img.id === id))
          .filter((img): img is NonNullable<typeof img> => img !== undefined);
        
        setImages(orderedImages);
        setLoading(false);
        setMainImageLoaded(true);
        setLoadedThumbnails(new Set(orderedImages.map(img => img.id)));
        setAnimationPhase('initial');
        loadedImageIdsRef.current = currentImageIdsKey;
        hasSignaledReadyRef.current = false;
        hasTriggeredPreloadAnimationRef.current = false;
        
        // Set current view image to the first image
        if (orderedImages.length > 0) {
          setCurrentViewImage(orderedImages[0]);
          setSelectedImageIds([orderedImages[0].id]);
        }
        
        fetchCompanyLogo();
        checkInstagramConnection();
        
        // Trigger animation after a small delay
        setTimeout(() => {
          setAnimationPhase('phase1');
        }, 50);
        return;
      }
      
      // Check if we have preloaded images for NEW imageIds only (generate more case)
      const existingImageIds = new Set(images.map(img => img.id));
      const newImageIds = imageIds.filter(id => !existingImageIds.has(id));
      const allNewIdsPreloaded = newImageIds.length > 0 && newImageIds.every(id => preloadedIdsSet.has(id));
      
      if (preloadedImages && preloadedImages.length > 0 && allNewIdsPreloaded && images.length > 0) {
        // Combine existing images with new preloaded images
        const newPreloadedImages = newImageIds
          .map(id => preloadedImages.find(img => img.id === id))
          .filter((img): img is NonNullable<typeof img> => img !== undefined);
        
        const combinedImages = [...images, ...newPreloadedImages];
        
        setImages(combinedImages);
        setLoading(false);
        setMainImageLoaded(true);
        setLoadedThumbnails(new Set(combinedImages.map(img => img.id)));
        setAnimationPhase('initial');
        loadedImageIdsRef.current = currentImageIdsKey;
        hasSignaledReadyRef.current = false;
        hasTriggeredPreloadAnimationRef.current = false;
        
        // Set current view image to the first NEW image
        if (newPreloadedImages.length > 0) {
          setCurrentViewImage(newPreloadedImages[0]);
          setSelectedImageIds([newPreloadedImages[0].id]);
        }
        
        fetchCompanyLogo();
        checkInstagramConnection();
        
        // Trigger animation after a small delay
        setTimeout(() => {
          setAnimationPhase('phase1');
        }, 50);
        return;
      }
      
      // Reset signalling
      hasSignaledReadyRef.current = false;
      // Reset and fetch normally (for "generate more" or non-preloaded case)
      setAnimationPhase('initial');
      setMainImageLoaded(false);
      setLoadedThumbnails(new Set());
      loadedImageIdsRef.current = currentImageIdsKey;
      fetchGeneratedImages();
      fetchCompanyLogo();
      checkInstagramConnection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageIds.join(','), companyId, preloadedImages]);

  // Trigger animation sequence when loading completes AND all images are loaded
  useEffect(() => {
    if (!loading && images.length > 0 && mainImageLoaded && allThumbnailsLoaded && animationPhase === 'initial') {
      // Start phase 1 when images are fully loaded
      requestAnimationFrame(() => {
        setAnimationPhase('phase1');
      });
    }
  }, [loading, images.length, mainImageLoaded, allThumbnailsLoaded, animationPhase]);

  // Set the first image as current view image when images load
  useEffect(() => {
    if (images.length > 0 && !currentViewImage) {
      setCurrentViewImage(images[0]);
    }
  }, [images, currentViewImage]);

  // Select the first image by default when images load and nothing is selected yet
  useEffect(() => {
    if (images.length > 0 && selectedImageIds.length === 0) {
      setSelectedImageIds([images[0].id]);
    }
  }, [images, selectedImageIds]);

  // Reset zoom level and position when fullscreen image changes
  useEffect(() => {
    if (fullScreenImage) {
      setZoomLevel(100);
      setImagePosition({ x: 0, y: 0 });
    }
  }, [fullScreenImage]);

  // Reset Instagram post status when closing the Instagram preview
  useEffect(() => {
    if (!showInstagramPreview) {
      setInstagramPostSuccess(false);
    }
  }, [showInstagramPreview]);

  const checkInstagramConnection = async () => {
    try {
      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user;
      if (!user) {
        setIsInstagramConnected(false);
        return;
      }

      // Check instagram_data in public schema keyed by id_company
      if (companyId) {
        const { data: instagramData } = await supabase
          .from('instagram_data')
          .select('instagram_username')
          .eq('id_company', companyId)
          .maybeSingle();

        if (instagramData?.instagram_username) {
          setIsInstagramConnected(true);
          return;
        }
      }

      setIsInstagramConnected(false);
    } catch (e) {
      console.error('Error checking Instagram connection:', e);
      setIsInstagramConnected(false);
    }
  };

  // Create optimized version of an image (same as full modal)
  const createOptimizedImage = async (imageUrl: string, fileName: string) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Failed to get canvas context');

      const resizePromise = new Promise<Blob>((resolve, reject) => {
        img.onload = () => {
          const targetWidth = 300;
          const targetHeight = 300;
          let width = img.width;
          let height = img.height;
          if (width > height) {
            if (width > targetWidth) {
              height = Math.round(height * (targetWidth / width));
              width = targetWidth;
            }
          } else {
            if (height > targetHeight) {
              width = Math.round(width * (targetHeight / height));
              height = targetHeight;
            }
          }
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('Failed to create image blob'))),
            'image/jpeg',
            0.8
          );
        };
        img.onerror = () => reject(new Error('Failed to load image for optimization'));
        img.src = URL.createObjectURL(blob);
      });

      const optimizedBlob = await resizePromise; // eslint-disable-line @typescript-eslint/no-unused-vars
      const optFilePath = `${companyId}/opt/${fileName}`;
      const { error: optUploadError } = await supabase.storage.from('generadas').upload(optFilePath, optimizedBlob);
      if (optUploadError) {
        console.error('Error uploading optimized image:', optUploadError);
      }
      URL.revokeObjectURL(img.src);
    } catch (err) {
      console.error('Error creating optimized image:', err);
    }
  };

  const fetchGeneratedImages = async () => {
    if (!companyId || imageIds.length === 0) return;
    try {
      setError(null);
      setLoading(true);

      // Deduplicate and preserve order
      const uniqueIds = Array.from(new Set(imageIds));
      const resultsMap = new Map<number, GeneratedImage>();
      const pending = new Set<number>(uniqueIds);

      const maxAttempts = 8; // Retry for ~8s total
      const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

      for (let attempt = 1; attempt <= maxAttempts && pending.size > 0; attempt++) {
        for (const imageId of Array.from(pending)) {
          try {
            const { data: imageData, error: imageError } = await supabase
              .from('imagenes_generadas')
              .select('file_name, favorito')
              .eq('id', imageId)
              .eq('id_company', companyId)
              .single();

            if (imageError) {
              console.warn(`Intento ${attempt}: error obteniendo imagen ${imageId}:`, imageError);
              continue;
            }

            const hasFileName = imageData?.file_name !== null && imageData?.file_name !== undefined;
            if (!hasFileName) {
              // El registro aún no tiene file_name; volver a intentar
              continue;
            }

            const filePath = `${companyId}/full/${imageData.file_name}`;
            const { data: signedUrlData } = await supabase.storage.from('generadas').createSignedUrl(filePath, 3600);
            if (!signedUrlData?.signedUrl) {
              // El archivo aún no está disponible en storage; volver a intentar
              continue;
            }

            resultsMap.set(imageId, {
              id: imageId,
              fileName: imageData.file_name,
              signedUrl: signedUrlData.signedUrl,
              favorito: imageData?.favorito === true,
            });

            // Crear versión optimizada en segundo plano
            createOptimizedImage(signedUrlData.signedUrl, imageData.file_name).catch((err) => console.error('Error creating optimized image:', err));

            pending.delete(imageId);
          } catch (innerErr) {
            console.error(`Intento ${attempt}: error inesperado para ${imageId}`, innerErr);
          }
        }

        // Actualizar incrementando y mantener el orden de entrada
        const ordered = uniqueIds
          .map((id) => resultsMap.get(id))
          .filter((v): v is GeneratedImage => Boolean(v));
        if (ordered.length > 0) {
          setImages(ordered);
          // En cuanto haya al menos una imagen, dejar de bloquear el UI del padre
          setLoading(false);
        }

        if (pending.size > 0) {
          await delay(1000);
        }
      }

      if (pending.size > 0) {
        console.warn('Algunas imágenes aún no están listas o no se pudieron cargar:', Array.from(pending));
      }

      // Actualización final para asegurar estado consistente
      const finalOrdered = uniqueIds
        .map((id) => resultsMap.get(id))
        .filter((v): v is GeneratedImage => Boolean(v));
      setImages(finalOrdered);
    } catch (err) {
      console.error('Error fetching generated images:', err);
      setError('Error al cargar las imágenes generadas. Por favor, inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSelected = async () => {
    const imagesToSave = images.filter((img) => selectedImageIds.includes(img.id));
    setIsDownloading(true);
    setDownloadSuccess(false);
    await saveImages(imagesToSave);
    setDownloadSuccess(true);
    setTimeout(() => {
      setIsDownloading(false);
      setDownloadSuccess(false);
    }, 3000);
  };

  const saveImages = async (imagesToSave: GeneratedImage[]) => {

    const inferExtFromMime = (mime?: string): string => {
      if (!mime) return '';
      const m = mime.toLowerCase();
      if (m === 'image/png') return 'png';
      if (m === 'image/jpeg') return 'jpg';
      if (m === 'image/webp') return 'webp';
      if (m === 'image/gif') return 'gif';
      if (m === 'image/bmp') return 'bmp';
      if (m === 'image/tiff') return 'tiff';
      if (m === 'image/svg+xml') return 'svg';
      return '';
    };

    const sanitizeDownloadFileName = (original: string, mime?: string): string => {
      let name = (original || 'imagen').trim();
      // Fix cases like filename.png_ or filename.jpg_
      name = name.replace(/\.(png|jpe?g|webp|gif|bmp|tiff|svg)_+$/i, '.$1');
      // Remove any trailing underscores just in case
      name = name.replace(/_+$/, '');
      // Ensure we end with a known image extension; if missing, infer from MIME or default to png
      const hasKnownExt = /\.(png|jpe?g|webp|gif|bmp|tiff|svg)$/i.test(name);
      if (!hasKnownExt) {
        const ext = inferExtFromMime(mime) || 'png';
        // If there is already some extension, keep it; otherwise append inferred
        if (!/\.[a-z0-9]+$/i.test(name)) {
          name = `${name}.${ext}`;
        }
      }
      return name;
    };

    for (const image of imagesToSave) {
      try {
        const response = await fetch(image.signedUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = sanitizeDownloadFileName(image.fileName, blob.type);
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        try {
          await supabase
            .from('imagenes_generadas')
            .update({ descargada: true })
            .eq('id', image.id)
            .eq('id_company', companyId);
        } catch {}
      } catch (error) {
        console.error(`Error downloading image ${image.fileName}:`, error);
      }
    }
  };

  const handlePostSelected = async () => {
    if (selectedImageIds.length === 0) return;
    const selectedImageId = selectedImageIds[0];
    const selectedImage = images.find((img) => img.id === selectedImageId);
    if (selectedImage) setCurrentViewImage(selectedImage);
    setShowInstagramPreview(true);
    if (!instagramPostSuccess) await generateDescription();
  };

  const getCurrentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const getBillingCycleRange = (paidPlanStartDate: string) => {
    const now = new Date();
    const subscriptionDate = new Date(paidPlanStartDate);
    const anchorDay = subscriptionDate.getDate();
    const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
    const anchorFor = (year: number, month: number) => {
      const dim = daysInMonth(year, month);
      const day = Math.min(anchorDay, dim);
      return new Date(year, month, day, 0, 0, 0, 0);
    };
    let start = anchorFor(now.getFullYear(), now.getMonth());
    if (start.getTime() > now.getTime()) {
      const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      start = anchorFor(prevMonth.getFullYear(), prevMonth.getMonth());
    }
    if (start.getTime() < subscriptionDate.getTime()) {
      start = new Date(subscriptionDate.getFullYear(), subscriptionDate.getMonth(), subscriptionDate.getDate(), 0, 0, 0, 0);
    }
    const nextMonth = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    const end = anchorFor(nextMonth.getFullYear(), nextMonth.getMonth());
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const checkSubscriptionLimitsForEdit = async (): Promise<boolean> => {
    try {
      if (!companyId) return true;
      const { data: companyProfile } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes, paid_plan_start_date')
        .eq('id_company', companyId)
        .single();
      if (!companyProfile) return true;
      // Block edit feature for 'emprendimiento' plan
      if ((companyProfile as any).plan === 'emprendimiento') {
        setShowEditPlanRestriction(true);
        return false;
      }
      const remaining = (companyProfile as any).imagenes_restantes as number | null | undefined;
      if (typeof remaining === 'number' && remaining <= 0) {
        const isFree = (companyProfile as any).plan === 'gratis';
        setSubscriptionLimitType(isFree ? 'free-trial-ended' : 'plan-limit-reached');
        setShowSubscriptionLimitModal(true);
        return false;
      }
      return true;
    } catch {
      return true;
    }
  };

  const handleEditSelected = async () => {
    const targetId = selectedImageIds[0] ?? currentViewImage?.id;
    if (!targetId) return;
    const canEdit = await checkSubscriptionLimitsForEdit();
    if (!canEdit) return;
    try { trackInteraction('imagenes_editadas'); } catch {}
    // Pass loaded images to avoid refetching when returning
    const cachedImages = images.map(img => ({
      id: img.id,
      fileName: img.fileName,
      signedUrl: img.signedUrl,
      favorito: img.favorito,
    }));
    navigate(
      `/redes-sociales/editar-imagen?id=${targetId}&companyId=${companyId}`,
      {
        state: {
          returnToResults: true,
          resultsImageIds: imageIds,
          resultsCompanyId: companyId,
          cachedImages,
        },
      }
    );
  };

  const handleFavoriteCurrent = async () => {
    const targetId = currentViewImage?.id ?? selectedImageIds[0];
    if (!targetId) return;
    setIsFavoriting(true);
    try {
      const current = images.find((img) => img.id === targetId);
      const nextValue = !(current?.favorito === true);
      const { error } = await supabase
        .from('imagenes_generadas')
        .update({ favorito: nextValue })
        .eq('id', targetId)
        .eq('id_company', companyId);
      if (error) {
        console.error('Error marcando como favorito:', error);
        alert('No se pudo marcar como favorito');
      } else {
        // Update local state
        setImages((prev) => prev.map((img) => (img.id === targetId ? { ...img, favorito: nextValue } : img)));
        setCurrentViewImage((prev) => (prev && prev.id === targetId ? { ...prev, favorito: nextValue } : prev));
        // Show success message briefly when marking as favorite
        if (nextValue) {
          setShowFavoriteMessage(true);
          setTimeout(() => setShowFavoriteMessage(false), 4000);
        }
      }
    } catch (e) {
      console.error('Error marcando como favorito:', e);
      alert('No se pudo marcar como favorito');
    } finally {
      setIsFavoriting(false);
    }
  };

  // Reset description panel when changing image
  useEffect(() => {
    setShowDescriptionPanel(false);
    setIsGeneratingDescription(false);
    setGeneratedDescription('');
    setCopiedDescription(false);
  }, [currentViewImage?.id]);

  // Trigger progress bar animation when generating description
  useEffect(() => {
    if (showDescriptionPanel && isGeneratingDescription) {
      setDescriptionProgressActive(false);
      const raf = requestAnimationFrame(() => setDescriptionProgressActive(true));
      return () => cancelAnimationFrame(raf);
    } else {
      setDescriptionProgressActive(false);
    }
  }, [showDescriptionPanel, isGeneratingDescription]);

  // Poll DB for post_description after webhook returns success
  const waitForPostDescription = async (
    idCompany: string,
    imageId: number,
    maxWaitMs = 15000,
    intervalMs = 800
  ): Promise<string | null> => {
    const deadline = Date.now() + maxWaitMs;
    while (Date.now() < deadline) {
      try {
        if (!currentViewImage || currentViewImage.id !== imageId) return null;
        const { data: imageData } = await supabase
          .from('imagenes_generadas')
          .select('post_description')
          .eq('id', imageId)
          .eq('id_company', idCompany)
          .single();
        const desc = (imageData as any)?.post_description as string | undefined;
        if (desc && desc.trim().length > 0) {
          return desc;
        }
      } catch {}
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    return null;
  };

  // Handle generating new angles from current image
  const handleGenerateNewAngles = async () => {
    if (!currentViewImage || isGeneratingNewAngles) return;
    setShowMoreOptions(false);
    setNewAnglesProgressComplete(false);
    setIsGeneratingNewAngles(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('No se pudo identificar al usuario');
        setNewAnglesProgressComplete(true);
        return;
      }

      // Check subscription limits before generating
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();
      
      const userCompanyId = profile?.id_company || companyId;
      if (userCompanyId) {
        const { data: companyProfile } = await supabase
          .from('company_profiles')
          .select('plan, imagenes_restantes')
          .eq('id_company', userCompanyId)
          .single();
        
        const remaining = (companyProfile as any)?.imagenes_restantes as number | null | undefined;
        if (typeof remaining === 'number' && remaining <= 0) {
          const isFree = (companyProfile as any)?.plan === 'gratis';
          setSubscriptionLimitType(isFree ? 'free-trial-ended' : 'plan-limit-reached');
          setShowSubscriptionLimitModal(true);
          setNewAnglesProgressComplete(true);
          return;
        }
      }

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/generar_nuevos_angulos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify({ user_id: user.id, image_id: currentViewImage.id }),
      });
      let newImageIds: string[] = [];
      try {
        const data = await response.json();
        // Server responds with array: [{ imagenes_generadas_ids: [...] }]
        // or object: { response: 'success', imagenes_generadas_ids: [...] }
        if (Array.isArray(data) && data[0]?.imagenes_generadas_ids) {
          newImageIds = data[0].imagenes_generadas_ids;
        } else if (data?.imagenes_generadas_ids) {
          newImageIds = data.imagenes_generadas_ids;
        }
      } catch {
        newImageIds = [];
      }

      if (newImageIds.length === 0) {
        setNewAnglesProgressComplete(true);
        alert('No se pudo generar nuevos ángulos. Intentá nuevamente.');
        return;
      }

      // Fetch the new images and add them to the current images
      const fetchNewImages = async () => {
        const newImages: GeneratedImage[] = [];
        const maxAttempts = 12;
        const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          for (const imageId of newImageIds) {
            // Skip if already fetched
            if (newImages.some(img => String(img.id) === String(imageId))) continue;

            try {
              const { data: imageData } = await supabase
                .from('imagenes_generadas')
                .select('file_name, favorito')
                .eq('id', imageId)
                .eq('id_company', userCompanyId)
                .single();

              if (!imageData?.file_name) continue;

              const filePath = `${userCompanyId}/full/${imageData.file_name}`;
              const { data: signedUrlData } = await supabase.storage.from('generadas').createSignedUrl(filePath, 3600);
              if (!signedUrlData?.signedUrl) continue;

              newImages.push({
                id: imageId,
                fileName: imageData.file_name,
                signedUrl: signedUrlData.signedUrl,
                favorito: imageData?.favorito === true,
              });

              // Create optimized version in background
              createOptimizedImage(signedUrlData.signedUrl, imageData.file_name).catch(() => {});
            } catch {}
          }

          if (newImages.length === newImageIds.length) break;
          await delay(1000);
        }

        return newImages;
      };

      const fetchedNewImages = await fetchNewImages();

      if (fetchedNewImages.length > 0) {
        // Select only the first new image
        const firstNewImage = fetchedNewImages[0];
        setSelectedImageIds([firstNewImage.id]);
        setCurrentViewImage(firstNewImage);
        
        // Add new images to existing images and mark thumbnails as loaded
        // Do this in a single batch to avoid triggering animation effects
        setImages(prev => [...prev, ...fetchedNewImages]);
        // Don't update loadedThumbnails here - let the onLoad handlers do it naturally
        // This prevents the animation from re-triggering for all images
      }

      setNewAnglesProgressComplete(true);
    } catch (_e) {
      setNewAnglesProgressComplete(true);
      alert('No se pudo generar nuevos ángulos. Intentá nuevamente.');
    }
  };

  // Handle generating Instagram description
  const handleGenerateDescription = async () => {
    if (!currentViewImage) return;
    setShowMoreOptions(false);
    setShowDescriptionPanel(true);
    setCopiedDescription(false);
    setIsGeneratingDescription(true);
    setGeneratedDescription('Generando descripción para post de Instagram');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setGeneratedDescription('No se pudo generar la descripción. Intentá nuevamente.');
        setIsGeneratingDescription(false);
        return;
      }
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();
      let idCompany = (profile as any)?.id_company ?? null;
      if (!idCompany && companyId) {
        idCompany = companyId;
      }
      if (!idCompany) {
        setGeneratedDescription('No se pudo generar la descripción. Intentá nuevamente.');
        setIsGeneratingDescription(false);
        return;
      }
      // Check if description already exists
      const { data: existing } = await supabase
        .from('imagenes_generadas')
        .select('post_description')
        .eq('id', currentViewImage.id)
        .eq('id_company', idCompany)
        .single();
      if ((existing as any)?.post_description) {
        setGeneratedDescription((existing as any).post_description as string);
        setIsGeneratingDescription(false);
        return;
      }
      // Call webhook to generate description
      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/descripcion_instagram', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify({ user_id: user.id, image_id: currentViewImage.id }),
      });
      let ok = false;
      try {
        const data = await response.json();
        ok = data?.response === 'success';
      } catch {
        ok = false;
      }
      if (ok) {
        const finalText = await waitForPostDescription(idCompany, currentViewImage.id);
        if (finalText) {
          setGeneratedDescription(finalText);
        } else {
          setGeneratedDescription('No se pudo obtener la descripción todavía. Probá nuevamente en unos segundos.');
        }
      } else {
        setGeneratedDescription('No se pudo generar la descripción. Intentá nuevamente.');
      }
    } catch (_e) {
      setGeneratedDescription('No se pudo generar la descripción. Intentá nuevamente.');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // Copy description to clipboard
  const copyGeneratedDescription = async () => {
    try {
      await navigator.clipboard.writeText(generatedDescription);
      setCopiedDescription(true);
      setTimeout(() => setCopiedDescription(false), 2000);
    } catch (err) {
      console.error('Error copying description:', err);
    }
  };

  const fetchCompanyLogo = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();
      if (profile?.id_company) {
        const { data: companyData } = await supabase
          .from('company_profiles')
          .select('logo_url, rubro')
          .eq('id_company', profile.id_company)
          .single();
        if (companyData?.logo_url) setCompanyLogo(companyData.logo_url);
        // Check if rubro is "Moda e Indumentaria"
        const rubro = (companyData as any)?.rubro as string | undefined;
        setIsFashionRubro(rubro === 'Moda e Indumentaria');
      }
    } catch (err) {
      console.error('Error fetching company logo:', err);
    }
  };

  const copyDescription = async () => {
    try {
      await navigator.clipboard.writeText(postDescription);
      alert('Descripción copiada al portapapeles');
    } catch (error) {
      console.error('Error al copiar la descripción:', error);
      alert('No se pudo copiar la descripción');
    }
  };

  const handlePostToInstagram = async () => {
    if (!currentViewImage) return;
    setIsPostingToInstagram(true);
    setInstagramPostSuccess(false);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('No se pudo identificar al usuario');
        setIsPostingToInstagram(false);
        return;
      }
      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/0d2c7586-b0b6-45c8-94e3-ab1ff6968e7c', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, imagen_id: currentViewImage.id, mode: 'post' })
      });
      const data = await response.json();
      if (data.response === 'success') {
        setInstagramPostSuccess(true);
      } else {
        alert('Error al publicar en Instagram');
      }
    } catch (error) {
      console.error('Error posting to Instagram:', error);
      alert('Error al publicar en Instagram');
    } finally {
      setIsPostingToInstagram(false);
    }
  };

  const generateDescription = async () => {
    if (!currentViewImage) return;
    setIsGeneratingDescription(true);
    setPostDescription('Generando descripción...');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('No se pudo identificar al usuario');
        setPostDescription('Moda fashion . veni a visitarnos');
        setIsGeneratingDescription(false);
        return;
      }
      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/0d2c7586-b0b6-45c8-94e3-ab1ff6968e7c', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, imagen_id: currentViewImage.id, mode: 'generate_description' })
      });
      const data = await response.json();
      if (data.response === 'success') {
        const { data: imageData, error } = await supabase
          .from('imagenes_generadas')
          .select('post_description')
          .eq('id', currentViewImage.id)
          .eq('id_company', companyId)
          .single();
        if (error) {
          console.error('Error fetching the description:', error);
          alert('Error al obtener la descripción generada');
          setPostDescription('Moda fashion . veni a visitarnos');
        } else if (imageData?.post_description) {
          setPostDescription(imageData.post_description);
        } else {
          setPostDescription('Moda fashion . veni a visitarnos');
        }
      } else {
        alert('Error al generar la descripción');
        setPostDescription('Moda fashion . veni a visitarnos');
      }
    } catch (error) {
      console.error('Error generating description:', error);
      alert('Error al generar la descripción');
      setPostDescription('Moda fashion . veni a visitarnos');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // Zoom & drag handlers
  const handleZoomIn = () => {
    if (zoomLevel < 300) setZoomLevel((prev) => Math.min(prev + 25, 300));
  };
  const handleZoomOut = () => {
    if (zoomLevel > 50) setZoomLevel((prev) => Math.max(prev - 25, 50));
  };
  const handleResetZoom = () => {
    setZoomLevel(100);
    setImagePosition({ x: 0, y: 0 });
  };
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel > 100) setDragStart({ x: e.clientX, y: e.clientY });
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (dragStart && zoomLevel > 100) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      setImagePosition((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };
  const handleMouseUp = () => setDragStart(null);

  const closeFullScreenView = () => {
    setFullScreenImage(null);
    setZoomLevel(100);
    setImagePosition({ x: 0, y: 0 });
  };

  // Instagram connect prompt portal
  const connectPrompt = showConnectPrompt && !isInstagramConnected
    ? createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100000] p-4" style={{ position: 'fixed' }}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowConnectPrompt(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center justify-center mb-4">
              <div className="bg-blue-100 p-3 rounded-full">
                <Instagram className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <h3 className="text-sm sm:text-lg font-semibold text-center mb-2">Conexión de Instagram<br className="sm:hidden" /> requerida</h3>
            <p className="text-gray-600 text-center mb-6">
              Para poder publicar el contenido generado, primero debes conectar la cuenta de Instagram de la empresa a Atlas.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => setShowConnectPrompt(false)} className="h-10 px-4 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50 transition-colors shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">Cancelar</button>
              <button onClick={() => { navigate('/configuracion', { state: { section: 'instagram' } }); }} className="h-10 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-full font-normal shadow-sm transition-colors flex items-center justify-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                <Instagram className="w-4 h-4" />
                Conectar Instagram
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  // Inline loading state: render nothing so parent overlay remains
  if (loading) return null;

  // Fullscreen image overlay (kept as overlay feature)
  if (fullScreenImage) {
    return (
      <>
        <div className="fixed inset-0 bg-black bg-opacity-90 z-[1000] flex flex-col">
          <div className="px-4 py-3 bg-gray-900 text-white flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button onClick={handleZoomOut} className="p-2 hover:bg-gray-700 rounded-full" disabled={zoomLevel <= 50}>
                <ZoomOut className="w-5 h-5" />
              </button>
              <div className="text-sm font-medium">{zoomLevel}%</div>
              <button onClick={handleZoomIn} className="p-2 hover:bg-gray-700 rounded-full" disabled={zoomLevel >= 300}>
                <ZoomIn className="w-5 h-5" />
              </button>
              <button onClick={handleResetZoom} className="p-2 hover:bg-gray-700 rounded-full">
                <Maximize className="w-5 h-5" />
              </button>
            </div>
            <button onClick={closeFullScreenView} className="p-2 hover:bg-gray-700 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center overflow-hidden" onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} ref={imageContainerRef}>
            <img src={fullScreenImage.signedUrl} alt={`Generated image ${fullScreenImage.id}`} style={{ transform: `scale(${zoomLevel / 100}) translate(${imagePosition.x}px, ${imagePosition.y}px)`, cursor: zoomLevel > 100 ? 'move' : 'default', transformOrigin: 'center', transition: dragStart ? 'none' : 'transform 0.2s ease-out' }} className="max-w-full max-h-full" />
          </div>
          <div className="px-4 py-3 bg-gray-900 text-white">
            <div className="flex justify-center space-x-4">
              <button onClick={() => saveImages([fullScreenImage])} className="h-10 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-full font-normal shadow-sm flex items-center space-x-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                <Download className="w-4 h-4" />
                <span>Guardar imagen</span>
              </button>
            </div>
          </div>
        </div>
        {connectPrompt}
      </>
    );
  }

  // Instagram preview overlay (kept as overlay feature)
  if (showInstagramPreview && currentViewImage) {
    if (instagramPostSuccess) {
      return (
        <>
          <div className="fixed inset-0 bg-gray-100 z-50 flex flex-col">
            <div className="px-4 py-1 sm:py-3 bg-white shadow-sm z-20 sticky top-0 flex justify-between items-center">
              <h2 className="text-sm sm:text-lg font-semibold text-gray-900">Instagram</h2>
              <button onClick={() => { setShowInstagramPreview(false); setInstagramPostSuccess(false); }} className="p-2 text-gray-500 hover:text-gray-700 rounded-md flex items-center" aria-label="Cerrar">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center p-6">
              <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full flex flex-col items-center">
                <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mb-6">
                  <Check className="w-16 h-16 text-green-500" />
                </div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-2">¡Publicado<br className="sm:hidden" /> con éxito!</h3>
                <p className="text-gray-600 text-center mb-6">Tu contenido ha sido publicado en Instagram correctamente.</p>
                <button onClick={() => { setShowInstagramPreview(false); setInstagramPostSuccess(false); }} className="h-10 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-full font-normal shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">Volver</button>
              </div>
            </div>
          </div>
          {connectPrompt}
        </>
      );
    }

    return (
      <>
        <div className="fixed inset-0 bg-gray-100 z-50 flex flex-col">
          <div className="px-4 py-1 sm:py-3 bg-white shadow-sm z-20 sticky top-0 flex justify-between items-center">
            <h2 className="text-sm sm:text-lg font-semibold text-gray-900">Vista previa<br className="sm:hidden" /> de Instagram</h2>
            <button onClick={() => setShowInstagramPreview(false)} className="p-2 text-gray-500 hover:text-gray-700 rounded-md flex items-center" aria-label="Cerrar">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto py-4 px-4 mt-2 relative">
            <div className="flex flex-col md:flex-row md:items-start md:justify-center md:space-x-6 max-w-5xl mx-auto pb-6">
              <div className="bg-white rounded-md shadow-sm w-full max-w-sm mx-auto md:mx-0 overflow-hidden mb-6 md:mb-0 flex flex-col">
                <div className="flex items-center p-3">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                    {companyLogo ? (
                      <img src={companyLogo} alt="Company logo" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                        {instagramUsername.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-sm">{instagramUsername}</p>
                  </div>
                </div>
                <div ref={imageContainerRef} className="w-full overflow-hidden" style={{ margin: 0, padding: 0, borderRadius: 0 }}>
                  <img src={currentViewImage.signedUrl} alt="Instagram post" className="w-full" style={{ width: '100%', maxHeight: 'min(700px, calc(100dvh - 200px))', display: 'block', margin: 0, padding: 0, borderRadius: 0 }} onLoad={(e) => {
                    const img = e.target as HTMLImageElement;
                    const aspectRatio = img.naturalWidth / img.naturalHeight;
                    const container = imageContainerRef.current;
                    if (!container) return;
                    if (aspectRatio === 1) {
                      container.style.aspectRatio = '1/1';
                      img.style.objectFit = 'contain';
                    } else if (aspectRatio > 1 && aspectRatio <= 1.91) {
                      container.style.aspectRatio = aspectRatio.toString();
                      img.style.objectFit = 'contain';
                    } else if (aspectRatio < 1 && aspectRatio >= 0.8) {
                      container.style.aspectRatio = aspectRatio.toString();
                      img.style.objectFit = 'contain';
                    } else if (aspectRatio < 0.8) {
                      container.style.aspectRatio = '0.8';
                      img.style.height = '100%';
                      img.style.width = 'auto';
                      img.style.maxWidth = 'none';
                      img.style.minWidth = '100%';
                      img.style.objectFit = 'cover';
                      img.style.objectPosition = 'center';
                      const scaledWidth = img.naturalHeight * 0.8; // 4:5
                      if (scaledWidth < img.naturalWidth) {
                        img.style.objectPosition = '50% 50%';
                      }
                    } else {
                      container.style.aspectRatio = '1.91';
                      img.style.objectFit = 'contain';
                    }
                  }} />
                </div>
                <div className="p-3 border-t border-gray-100 mt-auto">
                  <div className="flex space-x-4">
                    <button className="text-gray-800">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" />
                      </svg>
                    </button>
                    <button className="text-gray-800">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z" />
                      </svg>
                    </button>
                    <button className="text-gray-800">
                      <Share2 className="w-6 h-6" />
                    </button>
                  </div>
                  <div className="mt-2">
                    <p className="font-semibold text-sm">127 Me gusta</p>
                  </div>
                  <div className="mt-1">
                    {isEditingDescription ? (
                      <div className="space-y-2">
                        <textarea className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px]" value={editedDescription} onChange={(e) => setEditedDescription(e.target.value)} placeholder="Escribe la descripción aquí..." />
                        <div className="flex space-x-2">
                          <button onClick={() => { setPostDescription(editedDescription); setIsEditingDescription(false); }} className="h-9 px-4 inline-flex items-center justify-center bg-blue-500 text-white text-sm rounded-full font-normal shadow-sm hover:bg-blue-600 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">Guardar</button>
                          <button onClick={() => { setEditedDescription(postDescription); setIsEditingDescription(false); }} className="h-9 px-4 inline-flex items-center justify-center bg-gray-200 text-gray-800 text-sm rounded-full font-normal shadow-sm hover:bg-gray-300 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">Cancelar</button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm"><span className="font-semibold mr-1">{instagramUsername}</span>{postDescription}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="w-full max-w-sm mx-auto md:mx-0 md:w-60 md:flex-shrink-0 md:flex md:flex-col md:sticky md:top-1/2 md:-translate-y-1/2">
                <button onClick={() => { if (!isInstagramConnected) { setShowConnectPrompt(true); return; } handlePostToInstagram(); }} className={`${!isInstagramConnected ? 'bg-gray-200 text-gray-600 hover:bg-gray-300' : 'bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9]'} w-full mb-2 h-10 rounded-full font-normal transition-colors flex items-center justify-center gap-2 px-3 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`} disabled={isGeneratingDescription || isPostingToInstagram} aria-disabled={!isInstagramConnected} title={!isInstagramConnected ? 'Conectá tu cuenta de Instagram para publicar' : ''}>
                  {isPostingToInstagram ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      <span>Publicando...</span>
                    </>
                  ) : instagramPostSuccess ? (
                    <>
                      <Check className="w-4 h-4 flex-shrink-0" />
                      <span>Publicado con éxito</span>
                    </>
                  ) : !isInstagramConnected ? (
                    <>
                      <Lock className="w-4 h-4 flex-shrink-0" />
                      <span>Publicar en Instagram</span>
                    </>
                  ) : (
                    <>
                      <Instagram className="w-4 h-4 flex-shrink-0" />
                      <span>Publicar en Instagram</span>
                    </>
                  )}
                </button>
                <button onClick={() => { setEditedDescription(postDescription); setIsEditingDescription(true); }} className="w-full mb-2 h-10 bg-blue-500 text-white rounded-full font-normal hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 px-3 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 flex-shrink-0">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
                  </svg>
                  <span>Editar Descripción</span>
                </button>
                <button onClick={() => saveImages([currentViewImage!])} className="w-full mb-2 h-10 bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] rounded-full font-normal hover:bg-[#F1F5F9] transition-colors flex items-center justify-center gap-2 px-3 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  <Download className="w-4 h-4 flex-shrink-0" />
                  <span>Descargar</span>
                </button>
                <button onClick={copyDescription} className="w-full h-10 bg-blue-100 text-blue-700 border border-blue-300 rounded-full font-normal hover:bg-blue-200 transition-colors flex items-center justify-center gap-2 px-3 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  <Copy className="w-4 h-4 flex-shrink-0" />
                  <span>Copiar Descripción</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        {connectPrompt}
      </>
    );
  }

  // Main inline view
  return (
    <div className={`pt-2 pb-0 w-full bg-white sm:bg-transparent min-h-[100dvh] md:min-h-screen ${isStandalonePage ? 'px-4' : ''}`}>
      {/* Desktop layout: imagen izquierda, container derecha ocupa el resto */}
      <div className="hidden md:flex md:flex-row md:gap-4 md:items-start md:justify-center generated-images-desktop-height">
        {/* Imagen a la izquierda (desktop only) - altura fija, ancho auto */}
        <div 
          className={`flex-shrink-0 flex items-center justify-center generated-images-desktop-height relative transition-opacity duration-700 ease-out ${animationPhase === 'initial' ? 'opacity-0' : 'opacity-100'}`}
        >
          {showFavoriteMessage && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full shadow-sm">
                <Heart className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" />
                <span className="text-base font-medium text-gray-700 whitespace-nowrap">Imagen marcada como favorita</span>
              </div>
            </div>
          )}
          {currentViewImage ? (
            <img 
              src={currentViewImage.signedUrl} 
              alt={`Generated image ${currentViewImage.id}`} 
              style={{ height: 'calc(100vh - 8rem)', width: 'auto', maxWidth: 'calc(100vw - 500px)' }}
              className="object-contain rounded-lg"
              onLoad={() => setMainImageLoaded(true)}
            />
          ) : (
            <div className="text-gray-500">Selecciona una imagen</div>
          )}
        </div>

        {/* Container principal a la derecha (desktop only) - ocupa el resto del ancho, min 1/3, max 2/3 */}
        <div className={`flex-1 min-w-[33%] max-w-[50%] bg-white flex flex-col rounded-2xl border border-gray-200 overflow-hidden generated-images-desktop-height transition-opacity duration-700 ease-out ${animationPhase === 'initial' ? 'opacity-0' : 'opacity-100'}`}>
          {/* Header */}
          <div className={`relative px-6 py-4 bg-white z-10 flex justify-center items-center shadow-sm rounded-t-2xl transition-opacity duration-300 ease-out ${animationPhase === 'initial' || animationPhase === 'phase1' ? 'opacity-0' : 'opacity-100'}`}>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-gray-900 text-center">Imágenes Generadas</h2>
              <button className="inline-flex items-center justify-center cursor-pointer text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-full p-1" onClick={() => setShowInfoTooltip(true)} aria-label="Mostrar información">
                <Info size={18} />
              </button>
            </div>
            {(onReturnToConfig || onClose) && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (onReturnToConfig) {
                    onReturnToConfig();
                  } else if (onClose) {
                    onClose();
                  }
                }}
                className="absolute right-6 top-3 h-10 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-full font-normal shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Listo
              </button>
            )}
          </div>

          {error && (
            <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">{error}</div>
          )}

          {/* Thumbnails grid */}
          {images.length > 0 ? (
            <div className="flex-1 flex flex-col justify-between p-4">
              <div className="grid grid-cols-4 gap-3">
                {images.map((image, index) => (
                  <div
                    key={`desktop-thumb-${image.id}`}
                    className={`relative w-full aspect-square bg-white rounded-md shadow-sm overflow-hidden cursor-pointer transition-all duration-500 ease-out ${currentViewImage?.id === image.id ? 'ring-2 ring-blue-500 ring-offset-2' : ''} ${animationPhase === 'initial' ? 'opacity-0 translate-x-8' : 'opacity-100 translate-x-0'}`}
                    style={{ transitionDelay: animationPhase !== 'initial' ? `${index * 80}ms` : '0ms' }}
                    onClick={() => {
                      setCurrentViewImage(image);
                      setSelectedImageIds([image.id]);
                    }}
                  >
                    <img 
                      src={image.signedUrl} 
                      alt={`Generated image ${image.id}`} 
                      className="w-full h-full object-cover" 
                      onLoad={() => setLoadedThumbnails(prev => new Set(prev).add(image.id))}
                    />
                    {selectedImageIds.includes(image.id) && (
                      <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center">
                        <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    )}
                    {image.favorito === true && (
                      <div className="absolute top-1.5 right-1.5 z-10 pointer-events-none">
                        <Heart className="w-4 h-4 text-white drop-shadow" fill="currentColor" />
                      </div>
                    )}
                  </div>
                ))}
                {/* Generar más tile */}
                <button
                  type="button"
                  onClick={async () => {
                    if (!onGenerateMore || isGeneratingMore) return;
                    try { trackInteraction('generar_mas'); } catch {}
                    try {
                      setIsGeneratingMore(true);
                      await onGenerateMore();
                    } finally {
                      setIsGeneratingMore(false);
                    }
                  }}
                  disabled={isGeneratingMore}
                  className={`relative w-full aspect-square rounded-md shadow-sm overflow-hidden border border-transparent flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-500 ease-out ${isGeneratingMore ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 text-white'} ${animationPhase === 'initial' ? 'opacity-0 translate-x-8' : 'opacity-100 translate-x-0'}`}
                  style={{ transitionDelay: animationPhase !== 'initial' ? `${images.length * 80}ms` : '0ms' }}
                  aria-label="Generar más"
                >
                  <div className="flex flex-col items-center justify-center gap-2 text-white">
                    <Sparkles className="w-6 h-6" />
                    <span className="text-xs font-medium">Generar más</span>
                  </div>
                </button>
              </div>

              {/* Action buttons */}
              <div className="relative">
                {/* More options overlay */}
                {showMoreOptions && (
                  <>
                    <div className="fixed inset-0 z-[5]" onClick={() => setShowMoreOptions(false)} />
                    <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-xl border border-gray-200 shadow-lg p-3 z-10">
                    <button
                      type="button"
                      onClick={handleGenerateNewAngles}
                      disabled={!currentViewImage || isGeneratingNewAngles}
                      className="flex items-start gap-3 w-full p-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        {isGeneratingNewAngles ? (
                          <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                        ) : (
                          <Sparkles className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">Generar más imágenes como esta</p>
                        <p className="text-xs text-gray-500 mt-0.5">Creá más ángulos de la imagen seleccionada</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={handleGenerateDescription}
                      className="flex items-start gap-3 w-full p-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                        <Instagram className="w-5 h-5 text-pink-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">Generar descripción para Instagram</p>
                        <p className="text-xs text-gray-500 mt-0.5">Creá un caption atractivo para tu publicación</p>
                      </div>
                    </button>
                    <div
                      className="flex items-start gap-3 w-full p-3 rounded-lg bg-gray-50 text-left mt-1 opacity-60 cursor-not-allowed"
                    >
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <Video className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900">Generar video a partir de imagen</p>
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Próximamente</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">Convertí tu imagen en un video animado</p>
                      </div>
                    </div>
                    </div>
                  </>
                )}
                <div className={`flex items-center gap-2 mt-4 transition-opacity duration-300 ease-out ${animationPhase === 'initial' || animationPhase === 'phase1' ? 'opacity-0' : 'opacity-100'}`}>
                  <button
                    onClick={handleFavoriteCurrent}
                    disabled={isFavoriting || (!currentViewImage && selectedImageIds.length === 0)}
                    aria-pressed={(currentViewImage?.favorito === true) || (!currentViewImage && selectedImageIds.length > 0 && (images.find((img) => img.id === selectedImageIds[0])?.favorito === true))}
                    className={`w-10 h-10 rounded-full font-normal transition-colors flex items-center justify-center shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isFavoriting ? 'opacity-50 cursor-not-allowed' : ''} ${((currentViewImage?.favorito === true) || (!currentViewImage && selectedImageIds.length > 0 && (images.find((img) => img.id === selectedImageIds[0])?.favorito === true))) ? 'bg-blue-500 text-white hover:bg-blue-600 border border-transparent' : 'bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9]'}`}
                    aria-label="Favorito"
                  >
                    {isFavoriting ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                    ) : (
                      <Heart
                        className="w-4 h-4 flex-shrink-0"
                        fill={((currentViewImage?.favorito === true) || (!currentViewImage && selectedImageIds.length > 0 && (images.find((img) => img.id === selectedImageIds[0])?.favorito === true))) ? 'currentColor' : 'none'}
                      />
                    )}
                  </button>
                  <button onClick={handleEditSelected} disabled={!currentViewImage && selectedImageIds.length === 0} className={`flex-1 h-10 rounded-full font-normal flex items-center justify-center gap-2 px-3 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${(!currentViewImage && selectedImageIds.length === 0) ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9]'}`}>
                    <Pencil className="w-4 h-4 flex-shrink-0" />
                    <span className="whitespace-nowrap text-sm">Editar</span>
                  </button>
                  <button onClick={handleSaveSelected} disabled={selectedImageIds.length === 0} className={`flex-1 h-10 rounded-full font-normal flex items-center justify-center gap-2 px-3 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${selectedImageIds.length > 0 ? 'bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                    <Download className="w-4 h-4 flex-shrink-0" />
                    <span className="whitespace-nowrap text-sm">Descargar</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowMoreOptions(!showMoreOptions)}
                    className={`flex-1 h-10 rounded-full font-normal flex items-center justify-center gap-2 px-3 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${showMoreOptions ? 'bg-blue-500 text-white hover:bg-blue-600 border border-transparent' : 'bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9]'}`}
                  >
                    <Plus className="w-4 h-4 flex-shrink-0" />
                    <span className="whitespace-nowrap text-sm">Más</span>
                  </button>
                </div>
                {/* Description panel (desktop) */}
                {showDescriptionPanel && currentViewImage && (
                  <div className="absolute bottom-full left-0 right-0 mb-2 z-20">
                    <div className="relative bg-white rounded-2xl shadow-lg border border-gray-200 p-4">
                      <button
                        onClick={() => setShowDescriptionPanel(false)}
                        className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md"
                        aria-label="Cerrar"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      {isGeneratingDescription ? (
                        <div className="flex items-center gap-2 text-gray-700">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Generando descripción para post de Instagram</span>
                          <div className="absolute left-3 right-3 bottom-0 h-1 rounded-full overflow-hidden bg-gray-100">
                            <div
                              className="h-full bg-blue-500"
                              style={{ width: descriptionProgressActive ? '100%' : '0%', transition: 'width 20s cubic-bezier(0, 0, 0.2, 1)' }}
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          <h3 className="text-sm font-semibold text-gray-900 mb-2">Descripción para post</h3>
                          <div className="text-sm text-gray-800 whitespace-pre-wrap pr-6">{generatedDescription}</div>
                          <div className="mt-3 flex justify-end">
                            <button
                              onClick={copyGeneratedDescription}
                              className={`${copiedDescription ? 'bg-green-500 hover:bg-green-600' : 'bg-blue-500 hover:bg-blue-600'} h-9 px-4 text-white rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center gap-2`}
                            >
                              {copiedDescription ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                              {copiedDescription ? 'Copiado' : 'Copiar'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center py-16">
              <p className="text-gray-500">No se han generado imágenes</p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile layout */}
      <div className="md:hidden w-full bg-white">
        {/* Header */}
        <div className={`relative px-4 pt-4 py-0 bg-white z-10 flex justify-center items-center transition-opacity duration-300 ease-out ${animationPhase === 'initial' || animationPhase === 'phase1' ? 'opacity-0' : 'opacity-100'}`}>
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-gray-900 text-center">Imágenes<br /> Generadas</h2>
            <button className="inline-flex items-center justify-center cursor-pointer text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-full p-1" onClick={() => setShowInfoTooltip(true)} aria-label="Mostrar información">
              <Info size={18} />
            </button>
          </div>
          {(onReturnToConfig || onClose) && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onReturnToConfig) {
                  onReturnToConfig();
                } else if (onClose) {
                  onClose();
                }
              }}
              className="absolute right-0 top-1/2 -translate-y-1/2 h-10 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-full font-normal shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Listo
            </button>
          )}
        </div>

        {error && (
          <div className="mx-4 mt-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">{error}</div>
        )}

        {/* Main image */}
        {images.length > 0 ? (
          <div className="flex flex-col overflow-hidden pb-[calc(env(safe-area-inset-bottom,0px)+56px)] mt-3">
            <div className="flex-1 flex flex-col relative">
              {showFavoriteMessage && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full shadow-sm">
                    <Heart className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" />
                    <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Imagen marcada como favorita</span>
                  </div>
                </div>
              )}
              <div className={`bg-white flex-1 flex items-center justify-center p-1 transition-opacity duration-700 ease-out ${animationPhase === 'initial' ? 'opacity-0' : 'opacity-100'}`}>
                {currentViewImage ? (
                  <div className="w-full flex items-center justify-center relative min-h-[160px] h-[min(calc(100dvh-260px),80dvh)] max-h-[calc(100dvh-260px)]">
                    <img src={currentViewImage.signedUrl} alt={`Generated image ${currentViewImage.id}`} className="max-w-full max-h-full object-contain rounded-2xl" onLoad={() => setMainImageLoaded(true)} />
                  </div>
                ) : (
                  <div className="flex items-center justify-center w-full h-full text-gray-500">
                    <p>Selecciona una imagen para visualizarla</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-white py-16">
            <p className="text-gray-500">No se han generado imágenes</p>
          </div>
        )}

        {/* Action bar (mobile) */}
        <div className="px-4 pt-2 bg-white flex-shrink-0 z-20 fixed bottom-0 left-0 right-0" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}>
          {/* Mobile carousel */}
          {images.length > 0 && (
            <div className="mb-3 overflow-x-auto overflow-y-visible scrollbar-hide py-1">
              <div className="flex flex-nowrap justify-start space-x-3 min-w-max px-2" style={{ minHeight: '84px' }}>
                {images.map((image, index) => (
                  <div
                    key={`mobile-${image.id}`}
                    className={`relative flex-shrink-0 w-20 h-20 bg-white rounded-md shadow-sm overflow-hidden cursor-pointer transition-all duration-500 ease-out ${currentViewImage?.id === image.id ? 'ring-2 ring-blue-500' : ''} ${animationPhase === 'initial' ? 'opacity-0 translate-x-8' : 'opacity-100 translate-x-0'}`}
                    style={{ transitionDelay: animationPhase !== 'initial' ? `${index * 80}ms` : '0ms' }}
                    onClick={() => {
                      setCurrentViewImage(image);
                      setSelectedImageIds([image.id]);
                    }}
                  >
                    <img 
                      src={image.signedUrl} 
                      alt={`Generated image ${image.id}`} 
                      className="w-full h-full object-cover" 
                      onLoad={() => setLoadedThumbnails(prev => new Set(prev).add(image.id))}
                    />
                    {selectedImageIds.includes(image.id) && (
                      <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center">
                        <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    )}
                    {image.favorito === true && (
                      <div className="absolute top-1 right-1 z-10 pointer-events-none">
                        <Heart className="w-4 h-4 text-white drop-shadow" fill="currentColor" />
                      </div>
                    )}
                  </div>
                ))}
                {/* Generar más tile */}
                <button
                  type="button"
                  onClick={async () => {
                    if (!onGenerateMore || isGeneratingMore) return;
                    try { trackInteraction('generar_mas'); } catch {}
                    try {
                      setIsGeneratingMore(true);
                      await onGenerateMore();
                    } finally {
                      setIsGeneratingMore(false);
                    }
                  }}
                  disabled={isGeneratingMore}
                  className={`relative flex-shrink-0 w-32 h-20 rounded-md shadow-sm overflow-hidden border border-transparent flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-500 ease-out ${isGeneratingMore ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 text-white'} ${animationPhase === 'initial' ? 'opacity-0 translate-x-8' : 'opacity-100 translate-x-0'}`}
                  style={{ transitionDelay: animationPhase !== 'initial' ? `${images.length * 80}ms` : '0ms' }}
                  aria-label="Generar más"
                >
                  <div className="flex flex-row items-center justify-center gap-2 px-2 text-white">
                    <Sparkles className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm font-normal whitespace-nowrap">Generar más</span>
                  </div>
                </button>
              </div>
            </div>
          )}
          <div className="relative">
            {/* More options overlay (mobile) */}
            {showMoreOptions && (
              <>
                <div className="fixed inset-0 z-[5]" onClick={() => setShowMoreOptions(false)} />
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-xl border border-gray-200 shadow-lg p-3 z-10">
                <button
                  type="button"
                  onClick={handleGenerateNewAngles}
                  disabled={!currentViewImage || isGeneratingNewAngles}
                  className="flex items-start gap-3 w-full p-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    {isGeneratingNewAngles ? (
                      <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                    ) : (
                      <Sparkles className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">Generar más imágenes como esta</p>
                    <p className="text-xs text-gray-500 mt-0.5">Creá más ángulos de la imagen seleccionada</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={handleGenerateDescription}
                  className="flex items-start gap-3 w-full p-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                    <Instagram className="w-5 h-5 text-pink-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">Generar descripción para Instagram</p>
                    <p className="text-xs text-gray-500 mt-0.5">Creá un caption atractivo para tu publicación</p>
                  </div>
                </button>
                <div
                  className="flex items-start gap-3 w-full p-3 rounded-lg bg-gray-50 text-left mt-1 opacity-60 cursor-not-allowed"
                >
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Video className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900">Generar video a partir de imagen</p>
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Próximamente</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">Convertí tu imagen en un video animado</p>
                  </div>
                </div>
                </div>
              </>
            )}
            <div className={`flex flex-row justify-between gap-2 transition-opacity duration-300 ease-out ${animationPhase === 'initial' || animationPhase === 'phase1' ? 'opacity-0' : 'opacity-100'}`}>
              <button
                onClick={handleFavoriteCurrent}
                disabled={isFavoriting || (!currentViewImage && selectedImageIds.length === 0)}
                aria-pressed={(currentViewImage?.favorito === true) || (!currentViewImage && selectedImageIds.length > 0 && (images.find((img) => img.id === selectedImageIds[0])?.favorito === true))}
                className={`w-10 h-10 rounded-full font-normal transition-colors flex items-center justify-center shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isFavoriting ? 'opacity-50 cursor-not-allowed' : ''} ${((currentViewImage?.favorito === true) || (!currentViewImage && selectedImageIds.length > 0 && (images.find((img) => img.id === selectedImageIds[0])?.favorito === true))) ? 'bg-blue-500 text-white hover:bg-blue-600 border border-transparent' : 'bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9]'}`}
                aria-label="Favorito"
              >
                {isFavoriting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                ) : (
                  <Heart
                    className="w-4 h-4 flex-shrink-0"
                    fill={((currentViewImage?.favorito === true) || (!currentViewImage && selectedImageIds.length > 0 && (images.find((img) => img.id === selectedImageIds[0])?.favorito === true))) ? 'currentColor' : 'none'}
                  />
                )}
              </button>
              <button onClick={handleEditSelected} disabled={!currentViewImage && selectedImageIds.length === 0} className={`flex-1 h-10 rounded-full font-normal flex items-center justify-center gap-2 px-3 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${(!currentViewImage && selectedImageIds.length === 0) ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9]'}`}>
                <Pencil className="w-4 h-4 flex-shrink-0" />
                <span className="whitespace-nowrap text-sm">Editar</span>
              </button>
              <button onClick={handleSaveSelected} disabled={selectedImageIds.length === 0} className={`flex-1 h-10 rounded-full font-normal flex items-center justify-center gap-2 px-3 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${selectedImageIds.length > 0 ? 'bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                <Download className="w-4 h-4 flex-shrink-0" />
                <span className="whitespace-nowrap text-sm">Descargar</span>
              </button>
              <button
                type="button"
                onClick={() => setShowMoreOptions(!showMoreOptions)}
                className={`flex-1 h-10 rounded-full font-normal flex items-center justify-center gap-2 px-3 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${showMoreOptions ? 'bg-blue-500 text-white hover:bg-blue-600 border border-transparent' : 'bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9]'}`}
              >
                <Plus className="w-4 h-4 flex-shrink-0" />
                <span className="whitespace-nowrap text-sm">Más</span>
              </button>
            </div>
            {/* Description panel (mobile) */}
            {showDescriptionPanel && currentViewImage && (
              <div className="absolute bottom-full left-0 right-0 mb-2 z-20">
                <div className="relative bg-white rounded-2xl shadow-lg border border-gray-200 p-4">
                  <button
                    onClick={() => setShowDescriptionPanel(false)}
                    className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md"
                    aria-label="Cerrar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  {isGeneratingDescription ? (
                    <div className="flex items-center gap-2 text-gray-700">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Generando descripción para post de Instagram</span>
                      <div className="absolute left-3 right-3 bottom-0 h-1 rounded-full overflow-hidden bg-gray-100">
                        <div
                          className="h-full bg-blue-500"
                          style={{ width: descriptionProgressActive ? '100%' : '0%', transition: 'width 20s cubic-bezier(0, 0, 0.2, 1)' }}
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">Descripción para post</h3>
                      <div className="text-sm text-gray-800 whitespace-pre-wrap pr-6">{generatedDescription}</div>
                      <div className="mt-3 flex justify-end">
                        <button
                          onClick={copyGeneratedDescription}
                          className={`${copiedDescription ? 'bg-green-500 hover:bg-green-600' : 'bg-blue-500 hover:bg-blue-600'} h-9 px-4 text-white rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center gap-2`}
                        >
                          {copiedDescription ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          {copiedDescription ? 'Copiado' : 'Copiar'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Download Status Overlay */}
      {(isDownloading || downloadSuccess) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4 sm:px-0">
          <div className="bg-white rounded-md shadow-lg p-6 max-w-sm w-full mx-4 sm:mx-auto flex flex-col items-center">
            {isDownloading && !downloadSuccess && (
              <>
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mb-4"></div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-1">Preparando<br className="sm:hidden" /> descarga</h3>
              </>
            )}
            {downloadSuccess && (
              <>
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <Check className="w-12 h-12 text-green-500" />
                </div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-1">Imagen<br className="sm:hidden" /> descargada</h3>
              </>
            )}
          </div>
        </div>
      )}

      <SubscriptionLimitModal
        isOpen={showSubscriptionLimitModal}
        onClose={() => setShowSubscriptionLimitModal(false)}
        type={subscriptionLimitType}
      />

      {showEditPlanRestriction && (
        <div className="fixed inset-0 flex items-center justify-center z-[100000]" style={{ position: 'fixed' }}>
          <div className="fixed inset-0 bg-black bg-opacity-60" onClick={() => setShowEditPlanRestriction(false)} />
          <div className="relative bg-white rounded-2xl w-[95vw] max-w-md p-6 shadow-lg">
            <button
              onClick={() => setShowEditPlanRestriction(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-2">Editar con IA<br className="sm:hidden" /> disponible en Plan Pro o Ultra</h3>
            <p className="text-gray-700 mb-5">La función de editar con IA está disponible para el plan Pro o Ultra. Mejorá tu plan para acceder a esta función y triplicar tu límite de fotos mensual.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button onClick={() => setShowEditPlanRestriction(false)} className="h-10 px-4 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50 transition-colors shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">Cancelar</button>
              <button onClick={() => { setShowEditPlanRestriction(false); navigate('/subscription'); }} className="h-10 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-full font-normal shadow-sm transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">Ver planes</button>
            </div>
          </div>
        </div>
      )}

      {/* Info Tooltip Modal */}
      {showInfoTooltip && (
        <div className="fixed inset-0 flex items-center justify-center z-[99999]" style={{ position: 'fixed' }}>
          <div className="fixed inset-0 bg-black bg-opacity-60" onClick={() => setShowInfoTooltip(false)} />
          <div className="relative bg-white rounded-md w-[95vw] max-w-md max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-start p-4 border-b border-gray-100">
              <div className="w-8"></div>
              <h3 className="text-center font-semibold text-gray-900">Información de IA</h3>
              <button className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md p-1" onClick={() => setShowInfoTooltip(false)} aria-label="Cerrar información">
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              <AIInfoContent isFashion={isFashionRubro} />
            </div>
          </div>
        </div>
      )}

      {/* Removed local generating overlay; parent page overlay will remain visible */}

      {connectPrompt}

      {/* Loading animation for generating new angles */}
      {isGeneratingNewAngles && (
        <LoadingAnimation
          message="Generando más imágenes..."
          subMessage="Esto puede tardar unos segundos"
          durationMs={70000}
          completeNow={newAnglesProgressComplete}
          onCompleted={() => setIsGeneratingNewAngles(false)}
        />
      )}
    </div>
  );
};

export default GeneratedImagesInline;
